/**
 * Red banner while a payout alert is open (spec 0010 §7, item 22).
 *
 * Reads ``GET /admin-api/v1/payout-health/`` (scoped to the operator's
 * tenants) every 60 s. ``page``: an unacknowledged PayoutAlert, or a
 * payout unresolved for 30 min. The second stays red after its alert is
 * acknowledged, until the payout leaves ``processing``. ``warn``: parked
 * or unresolved payouts, or callbacks dead-lettered in the last 7 days.
 * Nothing renders when all is well. If the first read fails, a muted
 * "unknown" line says so instead of looking like "all clear"; a later
 * failed refresh keeps the last result. Never blocks the shell.
 *
 * One line by default: the count and the most urgent alert, which the API
 * sorts first. "Details" opens the other listed alerts (up to 5; the rest
 * are counted) and the runbook link. It can't be dismissed. Raw alert codes
 * stay visible because the operator runbook is indexed by them.
 *
 * Read-only: acknowledging is SUPER + step-up through
 * ``POST /admin-api/v1/payout-alerts/{id}/acknowledge/`` (operator
 * runbook); there is no acknowledge button yet.
 */
import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { Link } from "react-router-dom";
import { ChevronDown, Siren, TriangleAlert } from "lucide-react";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { getPayoutHealth, type PayoutAlertSummary } from "@/lib/api";
import { DOCS_BASE_URL } from "@/lib/docs";
import { cn } from "@/lib/utils";

const REFRESH_MS = 60_000;
const STUCK_PAGE_S = 30 * 60;

const ALERT_LABEL: Readonly<Record<string, string>> = {
  amount_mismatch: "Amount doesn't match",
  conflicting_result: "Conflicting results",
  correlation_mismatch: "Result couldn't be matched",
  credential_blocked: "Credential blocked",
  late_success_after_failure: "Success after failure",
  needs_operator_resolution: "Needs an operator",
  receipt_reuse: "Receipt reused",
  unresolved_30m: "Stuck for 30+ minutes",
};

function AlertSummary({ alert }: { alert: PayoutAlertSummary }) {
  return (
    <span className="inline-flex min-w-0 flex-wrap items-baseline gap-x-2 gap-y-0.5 text-sm">
      <span className="font-medium">{ALERT_LABEL[alert.kind] ?? alert.kind.replace(/_/g, " ")}</span>
      {alert.count > 1 ? <span className="text-xs">×{alert.count}</span> : null}
      {alert.intent_id ? (
        <Link
          className="font-mono text-xs underline underline-offset-2"
          to={`/payment-intents/${alert.intent_id}`}
        >
          {alert.intent_id}
        </Link>
      ) : (
        <span className="font-mono text-xs">{alert.tenant_id}</span>
      )}
      <span className="font-mono text-xs">{alert.kind}</span>
    </span>
  );
}

export function PayoutHealthBanner() {
  const query = useQuery({
    queryKey: ["payout-health"] as const,
    queryFn: getPayoutHealth,
    refetchInterval: REFRESH_MS,
  });
  const [expanded, setExpanded] = useState(false);
  const health = query.data;
  if (!health && query.isError) {
    return (
      <Alert className="mb-6 text-muted-foreground" data-testid="payout-health-unknown">
        <AlertDescription>
          Payout health unknown: couldn’t load it. Retrying every minute.
        </AlertDescription>
      </Alert>
    );
  }
  if (!health || !health.banner) return null;
  const stuck =
    health.oldest_unresolved_age_s !== null && health.oldest_unresolved_age_s >= STUCK_PAGE_S;

  if (health.banner === "page") {
    // The API sends critical alerts first.
    const top = health.latest_alerts[0];
    const notListed = health.open_alerts - health.latest_alerts.length;
    return (
      <div
        role="alert"
        data-testid="payout-health-banner"
        className="mb-3 flex animate-slide-up-fade gap-2.5 rounded-lg border border-destructive/30 bg-destructive/5 px-3 py-2 text-destructive shadow-sm dark:bg-destructive/10"
      >
        <Siren className="mt-0.5 h-4 w-4 shrink-0" aria-hidden />
        <div className="min-w-0 flex-1">
          <div className="flex items-start justify-between gap-3">
            <div className="flex min-w-0 flex-wrap items-center gap-x-3 gap-y-1">
              <h2 className="text-sm font-semibold">
                {health.open_alerts > 0
                  ? `${health.open_alerts} open payout alert${health.open_alerts === 1 ? "" : "s"}`
                  : "A payout has been unresolved for 30 minutes"}
              </h2>
              {top && !expanded ? <AlertSummary alert={top} /> : null}
            </div>
            <button
              type="button"
              aria-expanded={expanded}
              onClick={() => setExpanded((v) => !v)}
              className="inline-flex shrink-0 items-center gap-1 rounded text-xs font-medium underline-offset-2 hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-destructive"
            >
              {expanded ? "Hide details" : "Details"}
              <ChevronDown
                className={cn("h-3.5 w-3.5 transition-transform", expanded && "rotate-180")}
                aria-hidden
              />
            </button>
          </div>
          {expanded ? (
            <>
              {health.latest_alerts.length > 0 ? (
                <ul className="mt-1.5 space-y-1">
                  {health.latest_alerts.map((a) => (
                    <li key={a.id} className="flex flex-wrap items-baseline gap-x-2 text-sm">
                      <AlertSummary alert={a} />
                    </li>
                  ))}
                </ul>
              ) : null}
              {notListed > 0 ? (
                <p className="mt-1.5 text-xs font-medium">
                  Showing the {health.latest_alerts.length} most urgent. {notListed} more not listed.
                </p>
              ) : null}
              <p className="mt-1.5 text-xs">
                {stuck
                  ? "A payout has been processing for 30 minutes or more. This banner stays red until it settles, even after its alert is acknowledged. "
                  : null}
                Runbook:{" "}
                <a
                  className="underline underline-offset-2"
                  href={`${DOCS_BASE_URL}/operators/payout-alerts/`}
                  target="_blank"
                  rel="noreferrer"
                >
                  Payout alerts
                </a>
                . A super admin acknowledges each alert.
              </p>
            </>
          ) : null}
        </div>
      </div>
    );
  }

  return (
    <div
      role="alert"
      data-testid="payout-health-banner"
      className="mb-4 flex items-center gap-3 rounded-lg border border-warning/30 bg-warning/5 p-3 text-sm text-warning shadow-sm dark:bg-warning/10"
    >
      <TriangleAlert className="h-4 w-4 shrink-0" aria-hidden />
      <div className="min-w-0">
        <span className="font-semibold">Payouts need a look</span>
        <span className="ml-2">
          {health.unresolved_count} unresolved, {health.parked_count} parked,{" "}
          {health.needs_resolution_dead_letters} dead-lettered callback
          {health.needs_resolution_dead_letters === 1 ? "" : "s"}.
        </span>
      </div>
    </div>
  );
}

export default PayoutHealthBanner;
